import {
    AfterViewInit,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import { NgbActiveModal } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { delay, take } from 'rxjs/operators';

import { AbstractCmpsForm, CmpsFormFields } from '../common/common.form';
import { EMAIL_REGEXP } from '../common/common.validator';
import { ScreenReaderLiveService } from '../common/screen-reader-live.service';

import { TranslationKeyMap } from './auth.i18n';
import { AuthService } from './auth.service';

const EmailResetFormFields: CmpsFormFields = {
    EMAIL: {
        id: 'email',
        name: 'email',
        label: TranslationKeyMap._38_EMAIL,
        placeholder: TranslationKeyMap._38_EMAIL,
        validators: [
            Validators.required,
            Validators.pattern(EMAIL_REGEXP),
            Validators.maxLength(340),
        ],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            maxlength:
                TranslationKeyMap._7227__FIELD_LABEL__MUST_BE_LESS_THAN_OR_EQUAL_TO__REQUIREDLENGTH__CHARACTERS_,
            pattern:
                TranslationKeyMap._877_YOU_MUST_ENTER_A_VALID_AND_UNIQUE_EMAIL_ADDRESS__PLEASE_TRY_AGAIN__OR_CONTACT_YOUR_COMPAS_ADMINISTRATOR_,
        },
    },
};

class EmailResetForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(EmailResetFormFields, _translateService);
    }

    get name(): string {
        return 'emailResetForm';
    }
}

@Component({
    selector: 'cmps-auth-reset-email',
    templateUrl: './email-reset.auth.component.html',
})
export class EmailResetAuthComponent implements AfterViewInit, OnDestroy {
    @ViewChild('ngForm', { static: true })
    ngForm: NgForm;

    i18n = TranslationKeyMap;
    email: string;
    form: EmailResetForm;
    private _ngZoneSub: Subscription;
    private _sub: Subscription;
    success: boolean;
    failure: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _el: ElementRef,
        public activeModal: NgbActiveModal,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _screenReaderLiveService: ScreenReaderLiveService,
    ) {
        this.form = new EmailResetForm(_translateService);
    }

    ngAfterViewInit(): void {
        this._screenReaderLiveService.announce(
            this._translateService.instant(
                TranslationKeyMap._940_ENTER_YOUR_EMAIL_IN_THE_FORM_BELOW_AND_CLICK_THE__GET_PASSWORD__BUTTON_TO_HAVE_YOUR_CURRENT_PASSWORD_SENT_TO_YOUR_EMAIL_ADDRESS_ON_FILE_WITH_COMPAS_,
            ),
        );
        this._ngZoneSub = this._ngZone.onStable
            .asObservable()
            .pipe(take(1))
            .subscribe(() => {
                if (this.ngForm) {
                    this.form.form = this.ngForm;
                    try {
                        this.form.bind();
                        this._focusStep();
                    } catch (err) {
                        console.error(err);
                    }
                }
            });
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
        if (this._ngZoneSub) {
            this._ngZoneSub.unsubscribe();
        }
    }

    private _focusStep(): void {
        this._el.nativeElement.querySelector('#email').focus();
    }

    onSubmit(): void {
        this.form.validate();
        if (!this.form.isValid()) {
            return;
        }

        const languageId = parseInt(this._translateService.currentLang);

        this.failure = false;
        this.form.submitted = true;
        this._sub = this._authService
            .requestResetPasswordTokenByEmail(this.email, languageId)
            .pipe(delay(1000))
            // eslint-disable-next-line no-return-assign
            .subscribe(
                () => (this.success = true),
                (error) => {
                    this.failure = true;
                    this.form.serverError =
                        error.message || TranslationKeyMap._704_UNKNOWN_ERROR;
                    this.form.submitted = false;
                },
                // eslint-disable-next-line no-return-assign
                () => (this.form.submitted = false),
            );
    }
}
